import { memo, useRef, useState } from "react";
import {
  CENTER_TEXT_MAX_LENGTH,
  clampTextFontSize,
  sanitizeCenterTextInput,
  TEXT_FONT_SIZE_MAX,
  TEXT_FONT_SIZE_MIN,
  TEXT_FONT_SIZE_STEP,
} from "./centerTextRules";
import type { DesignMode, FrameShape, LightStyle } from "./types";
import { DEFAULT_LOGO_TRANSFORM, DEFAULT_TEXT_FONT_SIZE } from "./types";
import { createLogoDisplayUrl } from "./logoUpload";
import { setState as storeSetState, useConfigurator } from "./configuratorStore";

/**
 * Each section is its own `memo` component subscribing only to the state
 * slices it actually reads. The root `ConfiguratorPanelImpl` doesn't
 * subscribe at all — typing in the text-input only re-renders
 * `<TextDesignSection>`, dragging the brightness slider only re-renders
 * `<BrightnessSection>`, etc. DOM diffing for unrelated sections is
 * skipped entirely.
 *
 * `storeSetState` is the module-level setter from configuratorStore — its
 * identity is stable, so we call it directly instead of threading
 * `onChange` through props.
 */

const FRAME_COLORS: { hex: string; label: string; style?: React.CSSProperties }[] = [
  { hex: "#111111", label: "Black", style: { background: "#222" } },
  { hex: "#e8e8e8", label: "White" },
  { hex: "#c9a227", label: "Gold" },
  { hex: "#a0a0a0", label: "Silver" },
  { hex: "#c4a882", label: "Light Wood" },
  { hex: "#b76e79", label: "Rose Gold" },
];

const TEXT_COLORS: { hex: string; label: string }[] = [
  { hex: "#ffffff", label: "White" },
  { hex: "#ffe8c0", label: "Warm White" },
  { hex: "#ffff00", label: "Yellow" },
  { hex: "#ff8c00", label: "Orange" },
  { hex: "#ff2020", label: "Red" },
  { hex: "#00cc44", label: "Green" },
  { hex: "#00cccc", label: "Teal" },
  { hex: "#88ddff", label: "Ice Blue" },
  { hex: "#2255ff", label: "Blue" },
  { hex: "#aa00ff", label: "Purple" },
  { hex: "#ff44aa", label: "Pink" },
  { hex: "#ffaacc", label: "Light Pink" },
];

const LED_COLORS = TEXT_COLORS;

const SHAPES: { id: FrameShape; label: string }[] = [
  { id: "square", label: "Square" },
  { id: "circle", label: "Circle" },
  { id: "rectangle", label: "Rect" },
  { id: "vrect", label: "V-Rect" },
  { id: "hoval", label: "H-Oval" },
  { id: "voval", label: "V-Oval" },
];

const SIZES = [18, 24, 36, 48, 60, 72] as const;
const SIZE_LABELS = ["S 18″", "M 24″", "L 36″", "XL 48″", "XXL 60″", "3XL 72″"];

const FONTS = [
  { value: "system-ui, sans-serif", label: "Default Sans" },
  { value: "serif", label: "Serif" },
  { value: "monospace", label: "Mono" },
] as const;

const FrameShapeSection = memo(function FrameShapeSection() {
  const shape = useConfigurator((s) => s.shape);
  return (
    <section className="section">
      <div className="section-title">Frame Shape</div>
      <div className="shape-row">
        {SHAPES.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`shape-btn${shape === id ? " active" : ""}`}
            onClick={() => storeSetState({ shape: id })}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
});

const FrameColorSection = memo(function FrameColorSection() {
  const frameColor = useConfigurator((s) => s.frameColor);
  return (
    <section className="section">
      <div className="section-title">Frame Color</div>
      <div className="swatch-row">
        {FRAME_COLORS.map(({ hex, label, style }) => (
          <button
            key={hex}
            type="button"
            className={`swatch${frameColor === hex ? " active" : ""}`}
            title={label}
            style={{ background: style?.background ?? hex }}
            onClick={() => storeSetState({ frameColor: hex })}
          />
        ))}
      </div>
    </section>
  );
});

const SizeSection = memo(function SizeSection() {
  const shape = useConfigurator((s) => s.shape);
  const size = useConfigurator((s) => s.size);
  const sizePresetShort = useConfigurator((s) => s.sizePresetShort);
  const isRectLike =
    shape === "rectangle" || shape === "vrect" || shape === "hoval" || shape === "voval";
  return (
    <section className="section">
      <div className="section-title">Size</div>
      <div className="size-row">
        {SIZES.map((inch, i) => {
          const shortInch = isRectLike ? Math.max(1, Math.round(inch / 2)) : inch;
          return (
            <button
              key={inch}
              type="button"
              className={`size-btn${size === inch ? " active" : ""}`}
              onClick={() => storeSetState({ size: inch, sizePresetShort: shortInch })}
            >
              {SIZE_LABELS[i]}
            </button>
          );
        })}
      </div>
      <div className="label-row" style={{ marginTop: 4 }}>
        <span className="label">
          {size} × {sizePresetShort} inches
        </span>
      </div>
    </section>
  );
});

const TextDesignSection = memo(function TextDesignSection() {
  const centerText = useConfigurator((s) => s.centerText);
  const textColor = useConfigurator((s) => s.textColor);
  const textRgb = useConfigurator((s) => s.textRgb);
  const font = useConfigurator((s) => s.font);
  const textFontSize = useConfigurator((s) => s.textFontSize);
  return (
    <>
      <textarea
        className="text-input text-input--textarea"
        value={centerText}
        placeholder="Your text…"
        rows={3}
        maxLength={CENTER_TEXT_MAX_LENGTH}
        spellCheck={false}
        style={{ marginBottom: 6 }}
        onChange={(e) =>
          storeSetState({ centerText: sanitizeCenterTextInput(e.target.value) })
        }
      />
      <div className="label-row" style={{ marginBottom: 8 }}>
        <span className="label" style={{ fontSize: 11, opacity: 0.85 }}>
          {centerText.length} / {CENTER_TEXT_MAX_LENGTH} characters (max). Latin letters,
          numbers, and common punctuation only — other scripts are removed.
        </span>
      </div>
      <div className="label-row">
        <span className="label">Font</span>
      </div>
      <select
        className="select"
        style={{ marginBottom: 8 }}
        value={font}
        onChange={(e) => storeSetState({ font: e.target.value })}
      >
        {FONTS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>
      <div className="label-row" style={{ marginBottom: 4 }}>
        <span className="label">Font size</span>
        <span className="label" style={{ opacity: 0.75 }}>
          {textFontSize}px
        </span>
      </div>
      <div className="font-size-step-row" style={{ marginBottom: 8 }}>
        <button
          type="button"
          className="font-size-step-btn"
          title="Decrease font size"
          disabled={textFontSize <= TEXT_FONT_SIZE_MIN}
          onClick={() =>
            storeSetState({
              textFontSize: clampTextFontSize(textFontSize - TEXT_FONT_SIZE_STEP),
            })
          }
        >
          −
        </button>
        <input
          type="number"
          className="font-size-step-input"
          min={TEXT_FONT_SIZE_MIN}
          max={TEXT_FONT_SIZE_MAX}
          step={1}
          value={textFontSize}
          aria-label="Font size in pixels"
          onChange={(e) => {
            const v = Number.parseInt(e.target.value, 10);
            if (!Number.isFinite(v)) return;
            storeSetState({ textFontSize: clampTextFontSize(v) });
          }}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            if (raw === "") {
              storeSetState({ textFontSize: clampTextFontSize(DEFAULT_TEXT_FONT_SIZE) });
              return;
            }
            const v = Number.parseInt(raw, 10);
            if (!Number.isFinite(v)) {
              storeSetState({ textFontSize: clampTextFontSize(DEFAULT_TEXT_FONT_SIZE) });
            }
          }}
        />
        <button
          type="button"
          className="font-size-step-btn"
          title="Increase font size"
          disabled={textFontSize >= TEXT_FONT_SIZE_MAX}
          onClick={() =>
            storeSetState({
              textFontSize: clampTextFontSize(textFontSize + TEXT_FONT_SIZE_STEP),
            })
          }
        >
          +
        </button>
      </div>
      <div className="label-row" style={{ marginBottom: 4 }}>
        <span className="label" style={{ fontSize: 10, opacity: 0.65 }}>
          Range {TEXT_FONT_SIZE_MIN}–{TEXT_FONT_SIZE_MAX}px (step 1)
        </span>
      </div>
      <div className="section-title" style={{ marginTop: 8 }}>
        Text Color
      </div>
      <div className="swatch-row">
        {TEXT_COLORS.map(({ hex, label }) => (
          <button
            key={hex}
            type="button"
            className={`swatch${textColor === hex && !textRgb ? " active" : ""}`}
            title={label}
            style={{ background: hex }}
            onClick={() => storeSetState({ textColor: hex, textRgb: false })}
          />
        ))}
      </div>
      <div className="toggle-row" style={{ marginTop: 10 }}>
        <button
          type="button"
          className={`toggle-btn${!textRgb ? " active" : ""}`}
          onClick={() => storeSetState({ textRgb: false })}
        >
          Single color
        </button>
        <button
          type="button"
          className={`toggle-btn${textRgb ? " active" : ""}`}
          onClick={() => storeSetState({ textRgb: true })}
        >
          RGB +$30
        </button>
      </div>
    </>
  );
});

const LogoDesignSection = memo(function LogoDesignSection() {
  const logoImageUrl = useConfigurator((s) => s.logoImageUrl);
  const logoTransform = useConfigurator((s) => s.logoTransform);
  const logoRgb = useConfigurator((s) => s.logoRgb);

  const logoFileRef = useRef<HTMLInputElement>(null);
  // Editor-only counter for ±/rotation step. Lives here so editing it
  // doesn't push a re-render through the 3D scene — the value never
  // reaches it; it only scales the deltas applied via patchLogo.
  const [step, setStep] = useState(10);

  const lt = logoTransform;
  const patchLogo = (patch: Partial<typeof lt>) => {
    storeSetState({ logoTransform: { ...lt, ...patch } });
  };

  const openLogoPicker = () => {
    logoFileRef.current?.click();
  };

  const applyLogoFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const url = await createLogoDisplayUrl(file);
      storeSetState({
        logoImageUrl: url,
        logoAssetRevision: `${Date.now()}`,
        designMode: "logo",
        logoTransform: { ...DEFAULT_LOGO_TRANSFORM },
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load the file.";
      window.alert(msg);
    }
  };

  return (
    <>
      <input
        ref={logoFileRef}
        type="file"
        className="visually-hidden"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,.svg"
        onChange={(e) => {
          const f = e.target.files?.[0];
          void applyLogoFile(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        className="logo-drop-zone"
        onClick={openLogoPicker}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const f = e.dataTransfer.files?.[0];
          void applyLogoFile(f);
        }}
      >
        {logoImageUrl ? (
          <span className="logo-drop-zone__hint">Click to change image</span>
        ) : (
          <>
            <span className="logo-drop-zone__title">Upload logo</span>
            <span className="logo-drop-zone__hint">
              Click or drop PNG, JPEG, WebP, or SVG (max 30 MB)
            </span>
          </>
        )}
      </button>
      {logoImageUrl && (
        <div className="logo-preview-wrap">
          <img src={logoImageUrl} alt="Logo preview" className="logo-preview-thumb" />
          <div className="logo-tools-grid" aria-label="Logo image tools">
            <button
              type="button"
              className="logo-tool-btn"
              title="Delete image"
              onClick={() => storeSetState({ logoImageUrl: null })}
            >
              🗑
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Zoom in (scale %)"
              onClick={() =>
                patchLogo({ scalePercent: Math.min(400, lt.scalePercent + step) })
              }
            >
              +
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Zoom out (scale %)"
              onClick={() =>
                patchLogo({ scalePercent: Math.max(20, lt.scalePercent - step) })
              }
            >
              −
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Fit / reset alignment"
              onClick={() => storeSetState({ logoTransform: { ...DEFAULT_LOGO_TRANSFORM } })}
            >
              ⤢
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Rotate left (°)"
              onClick={() => patchLogo({ rotationDeg: lt.rotationDeg - step })}
            >
              ↺
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Rotate right (°)"
              onClick={() => patchLogo({ rotationDeg: lt.rotationDeg + step })}
            >
              ↻
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Move left (px)"
              onClick={() => patchLogo({ offsetPxX: lt.offsetPxX - step })}
            >
              ←
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Move right (px)"
              onClick={() => patchLogo({ offsetPxX: lt.offsetPxX + step })}
            >
              →
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Move up (px)"
              onClick={() => patchLogo({ offsetPxY: lt.offsetPxY + step })}
            >
              ↑
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Move down (px)"
              onClick={() => patchLogo({ offsetPxY: lt.offsetPxY - step })}
            >
              ↓
            </button>
            <button
              type="button"
              className="logo-tool-btn"
              title="Flip horizontal"
              onClick={() => patchLogo({ flipH: !lt.flipH })}
            >
              ⇄
            </button>
            <label className="logo-tool-step">
              <span className="visually-hidden">Step for px, degrees, percent</span>
              <input
                type="number"
                className="logo-tool-step-input"
                min={1}
                max={200}
                value={step}
                onChange={(e) => {
                  const v = Number.parseInt(e.target.value, 10);
                  setStep(Number.isFinite(v) ? Math.min(200, Math.max(1, v)) : 10);
                }}
              />
            </label>
          </div>
          <button
            type="button"
            className="logo-remove-bg"
            disabled
            title="У Shopify; у прев’ю не підключено"
          >
            Remove background
          </button>
        </div>
      )}
      {logoImageUrl && (
        <>
          <div className="section-title" style={{ marginTop: 10 }}>
            Logo tint
          </div>
          <div className="toggle-row" style={{ marginTop: 6 }}>
            <button
              type="button"
              className={`toggle-btn${!logoRgb ? " active" : ""}`}
              onClick={() => storeSetState({ logoRgb: false })}
            >
              Natural
            </button>
            <button
              type="button"
              className={`toggle-btn${logoRgb ? " active" : ""}`}
              onClick={() => storeSetState({ logoRgb: true })}
            >
              RGB +$30
            </button>
          </div>
        </>
      )}
    </>
  );
});

const MiddleDesignSection = memo(function MiddleDesignSection() {
  const designMode = useConfigurator((s) => s.designMode);
  return (
    <section className="section">
      <div className="section-title">Middle Design</div>
      <div className="toggle-row" style={{ marginBottom: 10 }}>
        {(
          [
            ["nothing", "None"],
            ["text", "Text"],
            ["logo", "Logo"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`toggle-btn${designMode === id ? " active" : ""}`}
            onClick={() => storeSetState({ designMode: id as DesignMode })}
          >
            {label}
          </button>
        ))}
      </div>

      {designMode === "text" && <TextDesignSection />}
      {designMode === "logo" && <LogoDesignSection />}
    </section>
  );
});

const SideLightingSection = memo(function SideLightingSection() {
  const lightStyle = useConfigurator((s) => s.lightStyle);
  const ledColor = useConfigurator((s) => s.ledColor);
  const rgb = useConfigurator((s) => s.rgb);
  return (
    <section className="section">
      <div className="section-title">Side Lighting Style</div>
      <div className="light-style-row" style={{ marginBottom: 10 }}>
        {(
          [
            ["bar", "Solid Bar"],
            ["text", "Text Edge"],
            ["point", "Point"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`light-btn${lightStyle === id ? " active" : ""}`}
            onClick={() => storeSetState({ lightStyle: id as LightStyle })}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="section-title">LED Color</div>
      <div className="swatch-row">
        {LED_COLORS.map(({ hex, label }) => (
          <button
            key={`led-${hex}`}
            type="button"
            className={`swatch${ledColor === hex ? " active" : ""}`}
            title={label}
            style={{ background: hex }}
            onClick={() => storeSetState({ ledColor: hex })}
          />
        ))}
      </div>
      <div className="toggle-row" style={{ marginTop: 10 }}>
        <button
          type="button"
          className={`toggle-btn${!rgb ? " active" : ""}`}
          onClick={() => storeSetState({ rgb: false })}
        >
          Single Color
        </button>
        <button
          type="button"
          className={`toggle-btn${rgb ? " active" : ""}`}
          onClick={() => storeSetState({ rgb: true })}
        >
          RGB +$30
        </button>
      </div>
    </section>
  );
});

const DepthLayersSection = memo(function DepthLayersSection() {
  const depthLayers = useConfigurator((s) => s.depthLayers);
  return (
    <section className="section">
      <div className="label-row">
        <span className="label">Depth Layers</span>
        <span className="label">{depthLayers}</span>
      </div>
      <input
        type="range"
        min={6}
        max={24}
        value={depthLayers}
        onChange={(e) => storeSetState({ depthLayers: Number(e.target.value) })}
      />
    </section>
  );
});

const BrightnessSection = memo(function BrightnessSection() {
  const brightness = useConfigurator((s) => s.brightness);
  return (
    <section className="section">
      <div className="label-row">
        <span className="label">LED Brightness</span>
        <span className="label">{brightness.toFixed(1)}</span>
      </div>
      <input
        type="range"
        min={0.2}
        max={2}
        step={0.1}
        value={brightness}
        onChange={(e) => storeSetState({ brightness: Number(e.target.value) })}
      />
    </section>
  );
});

/**
 * Panel root — no subscriptions, no props. Each section pulls its own slice
 * via `useConfigurator(s => s.X)` and re-renders independently. Typing in
 * the centerText textarea no longer diffs the swatch / size / lighting DOM.
 */
function ConfiguratorPanelImpl() {
  return (
    <aside className="panel">
      <h1 className="panel-title">Infinity Mirror</h1>
      <FrameShapeSection />
      <FrameColorSection />
      <SizeSection />
      <MiddleDesignSection />
      <SideLightingSection />
      <DepthLayersSection />
      <BrightnessSection />
    </aside>
  );
}

export const ConfiguratorPanel = memo(ConfiguratorPanelImpl);
